import type { Page } from "@/document/model/document";
import type { DocumentStyle } from "@/document/model/documentStyles";
import { pageGuides } from "@/document/model/guides";
import { cloneSceneNodeWithFreshIds } from "@/document/operations/cloneSceneNode";
import { createId } from "@/lib/ids";
import type { Command, CommandContext, CommandResult } from "./Command";

export class AddPageCommand implements Command {
  readonly id = "page.add";
  readonly label = "Add Page";
  private pageIndex?: number;

  constructor(private readonly page: Page) {}

  execute(context: CommandContext): CommandResult {
    this.pageIndex = context.document.pages.length;
    context.document.pages.push(structuredClone(this.page));
    return emptyResult();
  }

  undo(context: CommandContext): CommandResult {
    if (this.pageIndex === undefined) {
      throw new Error("Cannot undo page add; page index is missing.");
    }

    context.document.pages.splice(this.pageIndex, 1);
    return emptyResult();
  }

  redo(context: CommandContext): CommandResult {
    if (this.pageIndex === undefined) {
      return this.execute(context);
    }

    context.document.pages.splice(this.pageIndex, 0, structuredClone(this.page));
    return emptyResult();
  }
}

export class RenamePageCommand implements Command {
  readonly id = "page.rename";
  readonly label = "Rename Page";

  constructor(
    private readonly pageId: string,
    private readonly fromName: string,
    private readonly toName: string,
  ) {}

  execute(context: CommandContext): CommandResult {
    return this.applyName(context, this.toName);
  }

  undo(context: CommandContext): CommandResult {
    return this.applyName(context, this.fromName);
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }

  private applyName(context: CommandContext, name: string): CommandResult {
    const page = requirePage(context.document, this.pageId);
    page.name = name;
    return emptyResult();
  }
}

export class DeletePageCommand implements Command {
  readonly id = "page.delete";
  readonly label = "Delete Page";
  private pageIndex?: number;
  private removedPage?: Page;

  constructor(private readonly pageId: string) {}

  execute(context: CommandContext): CommandResult {
    if (context.document.pages.length <= 1) {
      throw new Error("Cannot delete the last page.");
    }

    const pageIndex = context.document.pages.findIndex(page => page.id === this.pageId);
    if (pageIndex < 0) {
      throw new Error("Cannot delete a missing page.");
    }

    this.pageIndex = pageIndex;
    this.removedPage = structuredClone(context.document.pages[pageIndex]);
    context.document.pages.splice(pageIndex, 1);
    return emptyResult();
  }

  undo(context: CommandContext): CommandResult {
    if (this.pageIndex === undefined || !this.removedPage) {
      throw new Error("Cannot undo page delete; removed page is missing.");
    }

    context.document.pages.splice(this.pageIndex, 0, structuredClone(this.removedPage));
    return emptyResult();
  }

  redo(context: CommandContext): CommandResult {
    if (this.pageIndex === undefined) {
      return this.execute(context);
    }

    context.document.pages.splice(this.pageIndex, 1);
    return emptyResult();
  }
}

export class DuplicatePageCommand implements Command {
  readonly id = "page.duplicate";
  readonly label = "Duplicate Page";
  private pageIndex?: number;

  constructor(
    private readonly sourcePageId: string,
    private readonly duplicatedPage: Page,
  ) {}

  execute(context: CommandContext): CommandResult {
    const sourceIndex = context.document.pages.findIndex(page => page.id === this.sourcePageId);
    if (sourceIndex < 0) {
      throw new Error("Cannot duplicate a missing page.");
    }

    this.pageIndex = sourceIndex + 1;
    context.document.pages.splice(this.pageIndex, 0, structuredClone(this.duplicatedPage));
    return emptyResult();
  }

  undo(context: CommandContext): CommandResult {
    if (this.pageIndex === undefined) {
      throw new Error("Cannot undo page duplicate; page index is missing.");
    }

    context.document.pages.splice(this.pageIndex, 1);
    return emptyResult();
  }

  redo(context: CommandContext): CommandResult {
    if (this.pageIndex === undefined) {
      return this.execute(context);
    }

    context.document.pages.splice(this.pageIndex, 0, structuredClone(this.duplicatedPage));
    return emptyResult();
  }
}

export class ImportComponentsPageCommand implements Command {
  readonly id = "page.importComponents";
  readonly label = "Import Components";
  private pageIndex?: number;
  private addedStyleIds: string[] = [];

  constructor(
    private readonly page: Page,
    private readonly styles: DocumentStyle[] = [],
  ) {}

  execute(context: CommandContext): CommandResult {
    this.pageIndex = context.document.pages.length;
    this.addedStyleIds = [];
    const existingStyleIds = new Set(context.document.styles.items.map(style => style.id));
    for (const style of this.styles) {
      if (existingStyleIds.has(style.id)) {
        continue;
      }
      context.document.styles.items.push(structuredClone(style));
      existingStyleIds.add(style.id);
      this.addedStyleIds.push(style.id);
    }
    context.document.pages.push(structuredClone(this.page));
    return emptyResult();
  }

  undo(context: CommandContext): CommandResult {
    if (this.pageIndex === undefined) {
      throw new Error("Cannot undo component import; page index is missing.");
    }

    context.document.pages.splice(this.pageIndex, 1);
    if (this.addedStyleIds.length > 0) {
      const added = new Set(this.addedStyleIds);
      context.document.styles.items = context.document.styles.items.filter(style => !added.has(style.id));
    }
    return emptyResult();
  }

  redo(context: CommandContext): CommandResult {
    if (this.pageIndex === undefined) {
      return this.execute(context);
    }

    const existingStyleIds = new Set(context.document.styles.items.map(style => style.id));
    for (const style of this.styles) {
      if (existingStyleIds.has(style.id)) {
        continue;
      }
      context.document.styles.items.push(structuredClone(style));
      existingStyleIds.add(style.id);
    }
    context.document.pages.splice(this.pageIndex, 0, structuredClone(this.page));
    return emptyResult();
  }
}

export function duplicatePage(source: Page, name: string): Page {
  return {
    id: createId("page"),
    name,
    children: source.children.map(child => cloneSceneNodeWithFreshIds(child, { renameRoot: false })),
    guides: pageGuides(source).map(guide => ({ ...guide, id: createId("guide") })),
  };
}

function requirePage(document: { pages: Page[] }, pageId: string): Page {
  const page = document.pages.find(candidate => candidate.id === pageId);
  if (!page) {
    throw new Error(`Page ${pageId} was not found.`);
  }
  return page;
}

function emptyResult(): CommandResult {
  return {
    changedNodeIds: [],
    affectedBounds: [],
  };
}
